/**
 * @module Experince/Product-Selector
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */

// Core Libraries
import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';

// Interfaces
import { ProductSelectorConfigData } from '@core/aem/interfaces/product-selector/product-selector.interface';

// Services
import { CommonUtilService } from '@support/shared/services/common-util.service';

// Models
import {
  LineDeivceDetails,
  lineTypeEnum,
} from '@experience/shop/product-selector/interfaces/product-selector.interface';
import { TileManagementService } from '@support/shared/services/tile-management.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { Cart } from '@support/api/proxies/responses/api-cart-response.interface';
import { PRODUCT_OFFERINGS_CONSTANTS } from '@core/aem/components/product-offerings/product-offerings.constants';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';

/**
 * Line Carousel component that displays the selected lines
 */
@Component({
  selector: 'tmo-line-carousel',
  templateUrl: './line-carousel.component.html',
})
export class LineCarouselComponent implements OnInit, OnDestroy {
  public isNoPlanNeeded: boolean;

  /**
   * Stores AEM data for line Carousel
   */
  @Input() public aemContent: ProductSelectorConfigData;
  public isNonLineItem: boolean;
  /**
   * Keeps track of the current Line details
   */
  @Input()
  public set focusedTileIdDetails(tileIDInFocusLineDetails: LineDeivceDetails) {
    if (tileIDInFocusLineDetails) {
      this.updateFocusedTileId(tileIDInFocusLineDetails);
    }
  }

  /**
   * Keeps track of the current lineId in focus details
   */
  public tileIDInFocusLineDetails: LineDeivceDetails;
  /**
   * Stores all line details
   */
  public allLineDetails: LineDeivceDetails[];
  /**
   * Holds max voice line length
   */
  public maxVoiceLineCount: number;
  /**
   * Total Lines remaining formatted AEM content
   */
  public totalLinesRemaining: string;
  /**
   * Holds current line item count
   */
  @Input() public currentVoiceLineCount: number;
  /**
   * Holds current non line item count
   */
  @Input() public nonLineItemCount: number;
  /**
   * Unsubscriber for all subscribtions
   */
  private ngUnsubscribe: Subject<void>;
  /**
   * Holds true to show line needs a device selection
   */
  public cartHasOnlyDevice: boolean;
  /**
   * Holds true to show line needs a plan selection
   */
  public cartHasOnlyPlan: boolean;
  /**
   * Holds true to show lline needs plan and device selection
   */
  public needPlanAndDevice: boolean;
  /**
   * Holds boolean to check if it is line tile or not
   */
  public isLine: number;
  /**
   * Holds e-num for existing customer
   */
  public isExisting: number;
  /**
   * Boolean to check if user is in upgrade flow
   */
  public isUpgrade: boolean;

  /**
   * Holds number of remaining lines eligible for customer
   */
  public remainingLines: number;
  /**
   * Boolean to show/hide spinner
   */
  public showSpinner: boolean;

  constructor(
    private tileManagementService: TileManagementService,
    private commonUtilService: CommonUtilService,
    private objectService: ObjectService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private router: Router
  ) {
    this.ngUnsubscribe = new Subject<void>();
  }
  /**
   * Called whenever this component is invoked from parent component
   * @returns void
   */
  public ngOnInit(): void {
    this.isUpgrade = this.stateStoreUtilsService.getUpgradeFlow();
    this.isLine = lineTypeEnum.LINE;
    this.isExisting = lineTypeEnum.EXISTING;
    this.initAEMData();
    this.getLineNonLineItemDetails();
    this.getUpdatedFocusedTileDetails();
    this.isNonLineItem = this.tileManagementService.isFocusedTileNonLineItem();
    this.aemContent.deviceNeedsAPlan = this.commonUtilService.messageFormatter(
      this.aemContent.deviceNeedsAPlan,
      ['1']
    );
    this.aemContent.lineNeedsADevice = this.commonUtilService.messageFormatter(
      this.aemContent.lineNeedsADevice,
      ['1']
    );
    this.aemContent.lineNeedsPlanAndDeviceSelection = this.commonUtilService.messageFormatter(
      this.aemContent.lineNeedsPlanAndDeviceSelection,
      ['1']
    );
  }

  /**
   * Destroys all the subscriptions
   * @returns void
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Formats AEM data baeed on input content
   * @returns void
   */
  private initAEMData(): void {
    this.totalLinesRemaining = this.commonUtilService.messageFormatter(
      this.aemContent.remainingLinesMessage,
      ['']
    );
  }

  public updateFocusedTileId(tileIDInFocusLineDetails: LineDeivceDetails): void {
    this.isNoPlanNeeded = this.objectService.get(tileIDInFocusLineDetails, 'noPlanNeeded');
  }
  public getMaxVoiceLines(): void {
    const totalLines = 5;
    const updatedCart: Cart = this.stateStoreUtilsService.getCart();
    this.maxVoiceLineCount = this.objectService.get(updatedCart, 'linesTotal') || totalLines;
    const linesRemaining = this.objectService.get(updatedCart, 'linesRemaining');
    this.remainingLines = linesRemaining >= 0 ? linesRemaining : totalLines;
  }

  private getLineNonLineItemDetails(): void {
    this.tileManagementService.getLineNonLineItemDetails
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((data: LineDeivceDetails[]) => {
        this.allLineDetails = data;
        this.isNonLineItem = this.tileManagementService.isFocusedTileNonLineItem();
      });
  }

  /**
   * Function to update line summary
   */
  public updateLineSummary(): void {
    const isPlanSelected =
      this.objectService.get(this.tileIDInFocusLineDetails, 'noPlanNeeded') ||
      this.objectService.get(this.tileIDInFocusLineDetails, 'plansFromCart.planOfferName');
    const isDeviceSelected =
      this.objectService.get(this.tileIDInFocusLineDetails, 'selectedDevice.id') ||
      this.objectService.get(this.tileIDInFocusLineDetails, 'byodSimData') ||
      this.objectService.get(this.tileIDInFocusLineDetails, 'byodCMMOrImei');
    const isExistingCustomer = this.tileIDInFocusLineDetails.lineTileType === this.isExisting;
    this.cartHasOnlyDevice = !isPlanSelected && !!isDeviceSelected && !isExistingCustomer;
    this.cartHasOnlyPlan = !!isPlanSelected && !isDeviceSelected && !isExistingCustomer;
    this.needPlanAndDevice = !isPlanSelected && !isDeviceSelected && !isExistingCustomer;
  }

  /**
   * Function to get updated focued tile details
   */
  private getUpdatedFocusedTileDetails(): void {
    this.tileManagementService.focusedTileDetails
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((data: LineDeivceDetails) => {
        if (data) {
          this.tileIDInFocusLineDetails = data;
          this.updateLineSummary();
          this.getMaxVoiceLines();
        }
      });
  }

  /**
   * Function used to navigate to selected tile
   * @param {LineDeivceDetails} tile
   */
  public navigateToSelectedTile(tile: LineDeivceDetails): void {
    this.router.navigate([PRODUCT_OFFERINGS_CONSTANTS.NAVIGATE_TO_PRODUCT_SELECTOR, tile.tileId]);
    this.tileIDInFocusLineDetails = this.stateStoreUtilsService.getFocusedTileLineNonLineItem(
      tile.tileId
    );
    this.tileIDInFocusLineDetails.getUpdatedPlans = false;
    this.tileManagementService.focusedTileDetails.next(this.tileIDInFocusLineDetails);
  }
}
